/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {Grid, makeStyles} from '@material-ui/core';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';

interface DeviceDetailsGridSectionProps {
  children: any;
  sectionTitle: string;
  headerAttachments?: JSX.Element;
}

const useStyles = makeStyles(() => ({
  formSection : {
    borderBottom : `1px solid ${dlsColor('grey4')}`,
    padding      : '32px 0px'
  },
  headerContainer : {
    display       : 'flex',
    flexDirection : 'row',
    alignItems    : 'center',
    width         : '100%'
  },
  formTitle : {
    ...dlsAlt('typography', 'cta3'),
    textTransform : 'uppercase',
    padding       : '0px 16px 0px'
  },
  gridContainer : {
    width : '100%'
  }
}));

/**
 * A grid used to make up a section of the Device Details tab.
 * 
 * ```typescript
 * 
 *   import DeviceDetailsGridSection from './DeviceDetailsGridSection';
 * 
 *   <DeviceDetailsGridSection sectionTitle={translate('admin-devices:view.deviceDetailSummary.favoritesSectionTitle')}>
 *     {gridItems}
 *   </DeviceDetailsGridSection>
 * 
 * ```
 * 
 * @param sectionTitle A title to put at the top of the section.
 * @param children     Items to render inside the grid.
 */
const DeviceDetailsGridSection = ({
  children,
  sectionTitle,
  headerAttachments = null
}: DeviceDetailsGridSectionProps) => {
  const classes = useStyles();
  
  return (
    <div className={classes.formSection}>
      <div className={classes.headerContainer}>
        <div className={classes.formTitle}>
          {sectionTitle}
        </div>
        {headerAttachments}
      </div>
      
      <Grid container
        spacing={0}
        alignItems='flex-start'
        justifyContent='flex-start'
        className={classes.gridContainer}>
        {children}
      </Grid>
    </div>
  );
};

export default DeviceDetailsGridSection;
